import { useEffect, useRef, useState } from "react";
import { useApiFetch } from "../../api/client";
import { Btn } from "../../components/ui/Button";
import { Chip } from "../../components/ui/Chip";
import { errorMessage } from "../cockpit/format";
import { formatLocation, reviewLabel, reviewSummary, segmentVariant, updatedLabel } from "./format";
import { GroupPickerDialog } from "./GroupPickerDialog";
import { groupNames, setFacilityGroups } from "./groupsApi";
import { listPath, pageRange, type FacilityFilters } from "./query";
import { setReview } from "./reviewApi";
import type { FacilityDetail, FacilityPage, FacilityReviewStatus, FacilitySummary, PlantNameChange } from "./types";

interface Props {
  filters: FacilityFilters;
  canReview: boolean;
  onOpen: (registryId: string) => void;
  onReviewed: () => void;
}

interface Notice {
  text: string;
  registryId: string;
  previous: FacilityReviewStatus;
}

const PAGE_SIZE = 100;
const ACTION_BUTTON =
  "inline-flex items-center justify-center bg-transparent border border-rs-jet-light rounded p-1 text-rs-light-gray disabled:opacity-40 disabled:cursor-not-allowed";

type PickMode = "verify" | "edit";

function RowActions({
  facility,
  busy,
  onSet,
  onPick,
}: {
  facility: FacilitySummary;
  busy: boolean;
  onSet: (status: FacilityReviewStatus) => void;
  onPick: (mode: PickMode) => void;
}) {
  const status = facility.reviewStatus;
  const editGroups = (status === "Verified" || facility.polymerGroups.length > 0) && (
    <button
      className={`${ACTION_BUTTON} hover:text-rs-green hover:border-rs-green`}
      aria-label={`Edit groups for ${facility.name}`}
      title="Edit polymer groups"
      disabled={busy}
      onClick={() => onPick("edit")}
    >
      <span className="material-symbols-sharp text-[18px]">category</span>
    </button>
  );
  return (
    <div className="flex gap-1.5">
      {status === "Rejected" ? (
        <>
          {editGroups}
          <button
            className={`${ACTION_BUTTON} hover:text-rs-green hover:border-rs-green`}
            aria-label={`Restore ${facility.name}`}
            title="Restore to the registry"
            disabled={busy}
            onClick={() => onSet("Unclassified")}
          >
            <span className="material-symbols-sharp text-[18px]">restore_from_trash</span>
          </button>
        </>
      ) : (
        <>
          {status === "Verified" ? (
            <>
              {editGroups}
              <button
                className={`${ACTION_BUTTON} hover:text-rs-off-white hover:border-rs-off-white`}
                aria-label={`Remove verification from ${facility.name}`}
                title="Remove ResinSmart Verified"
                disabled={busy}
                onClick={() => onSet("Unclassified")}
              >
                <span className="material-symbols-sharp text-[18px]">remove_done</span>
              </button>
            </>
          ) : (
            <>
              {editGroups}
              <button
                className={`${ACTION_BUTTON} hover:text-rs-green hover:border-rs-green`}
                aria-label={`Verify ${facility.name}`}
                title="Mark as ResinSmart Verified"
                disabled={busy}
                onClick={() => onPick("verify")}
              >
                <span className="material-symbols-sharp text-[18px]">verified</span>
              </button>
            </>
          )}
          <button
            className={`${ACTION_BUTTON} hover:text-rs-red hover:border-rs-red`}
            aria-label={`Remove ${facility.name} from the registry view`}
            title="Not a facility we care about: hide it"
            disabled={busy}
            onClick={() => onSet("Rejected")}
          >
            <span className="material-symbols-sharp text-[18px]">delete</span>
          </button>
        </>
      )}
    </div>
  );
}

export function FacilityTable({ filters, canReview, onOpen, onReviewed }: Props) {
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<FacilityPage | null>(null);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [reload, setReload] = useState(0);
  const [picker, setPicker] = useState<{ facility: FacilitySummary; mode: PickMode } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    (async () => {
      try {
        const response = await apiFetchRef.current(listPath(filters, PAGE_SIZE, offset));
        const body: FacilityPage = await response.json();
        if (!cancelled) {
          setPage(body);
          setLoadedAt(new Date());
        }
      } catch (err) {
        if (!cancelled) setError(errorMessage(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filters, offset, reload]);

  const apply = async (
    facility: FacilitySummary,
    status: FacilityReviewStatus,
    groupIds?: readonly number[],
    rename?: PlantNameChange,
  ) => {
    setBusyId(facility.registryId);
    setError(null);
    try {
      const updated = await setReview(apiFetchRef.current, facility.registryId, status, undefined, groupIds, rename);
      setPage((current) => {
        if (!current) return current;
        const visible = filters.reviewStatuses.includes(updated.reviewStatus);
        const items = visible
          ? current.items.map((item) => (item.registryId === facility.registryId ? { ...item, ...updated } : item))
          : current.items.filter((item) => item.registryId !== facility.registryId);
        return { ...current, items, total: visible ? current.total : Math.max(0, current.total - 1) };
      });
      setNotice({
        text:
          status === "Rejected"
            ? `${updated.name} was removed from the view.`
            : status === "Verified"
              ? `${updated.name} is now ${reviewLabel("Verified")}.`
              : `${updated.name} is now ${status}.`,
        registryId: facility.registryId,
        previous: facility.reviewStatus,
      });
      onReviewed();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const mergeUpdated = (updated: FacilityDetail) =>
    setPage((current) =>
      current
        ? {
            ...current,
            items: current.items.map((item) =>
              item.registryId === updated.registryId ? { ...item, ...updated } : item,
            ),
          }
        : current,
    );

  const saveGroups = async (facility: FacilitySummary, groupIds: readonly number[]) => {
    setBusyId(facility.registryId);
    setError(null);
    try {
      mergeUpdated(await setFacilityGroups(apiFetchRef.current, facility.registryId, groupIds));
      setNotice(null);
      onReviewed();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const confirmPick = (groupIds: number[], rename?: PlantNameChange) => {
    if (!picker) return;
    const { facility, mode } = picker;
    setPicker(null);
    if (mode === "verify") void apply(facility, "Verified", groupIds, rename);
    else void saveGroups(facility, groupIds);
  };

  const undo = async () => {
    if (!notice) return;
    setBusyId(notice.registryId);
    setError(null);
    try {
      await setReview(apiFetchRef.current, notice.registryId, notice.previous);
      setNotice(null);
      onReviewed();
      setReload((n) => n + 1);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  if (error && !page) return <p className="text-rs-red text-[13px]">{error}</p>;

  const columns = canReview ? 8 : 7;

  return (
    <>
      {error && <p className="text-rs-red text-[13px] mt-0 mb-3">{error}</p>}
      {notice && (
        <p className="m-0 mb-3 text-[13px] text-rs-light-gray" role="status">
          {notice.text}{" "}
          <button className="bg-transparent border-none p-0 ml-1 text-rs-green" onClick={() => void undo()}>
            Undo
          </button>
        </p>
      )}
      <div className="card p-0 overflow-auto max-w-full">
        <table>
          <thead>
            <tr>
              <th>Facility</th>
              <th>Location</th>
              <th>Segment</th>
              <th>Groups</th>
              <th>Primary NAICS</th>
              <th>Supplier</th>
              <th>Coordinates</th>
              {canReview && <th>Review</th>}
            </tr>
          </thead>
          <tbody>
            {!page && (
              <tr>
                <td colSpan={columns}>Loading…</td>
              </tr>
            )}
            {page?.items.length === 0 && (
              <tr>
                <td colSpan={columns}>No facilities match these filters.</td>
              </tr>
            )}
            {page?.items.map((facility) => (
              <tr key={facility.registryId}>
                <td>
                  <button
                    className="bg-transparent border-none p-0 text-left text-rs-off-white hover:text-rs-green"
                    onClick={() => onOpen(facility.registryId)}
                  >
                    {facility.name}
                  </button>
                  {facility.dataSource === "Manual" && (
                    <Chip className="ml-2" variant="info">
                      Manual
                    </Chip>
                  )}
                  {facility.candidateReason && (
                    <Chip className="ml-2" variant="info">
                      Possible match
                    </Chip>
                  )}
                  {facility.isRetired && (
                    <Chip className="ml-2" variant="bad">
                      Retired
                    </Chip>
                  )}
                </td>
                <td>{formatLocation(facility.city, facility.stateCode)}</td>
                <td>
                  <div className="flex flex-wrap gap-1.5">
                    <Chip variant={segmentVariant(facility.segment)}>{facility.segment}</Chip>
                    {facility.reviewStatus === "Verified" && (
                      <Chip
                        variant="ok"
                        title={
                          reviewSummary(facility.reviewStatus, facility.reviewedByName, facility.reviewedUtc) ??
                          undefined
                        }
                      >
                        {reviewLabel("Verified")}
                      </Chip>
                    )}
                    {facility.reviewStatus === "Rejected" && (
                      <Chip
                        variant="bad"
                        title={
                          reviewSummary(facility.reviewStatus, facility.reviewedByName, facility.reviewedUtc) ??
                          undefined
                        }
                      >
                        {reviewLabel("Rejected")}
                      </Chip>
                    )}
                  </div>
                </td>
                <td className="max-w-[260px] text-[12.5px]" title={groupNames(facility.polymerGroups) || undefined}>
                  <span className="block truncate">{groupNames(facility.polymerGroups) || "—"}</span>
                </td>
                <td className="mono">{facility.primaryNaicsCode ?? "—"}</td>
                <td>{facility.supplierName ?? "—"}</td>
                <td>
                  {facility.latitude === null ? (
                    <Chip variant="bad">None</Chip>
                  ) : facility.geocodeSource === "Azure" ? (
                    <Chip variant="blue">Address</Chip>
                  ) : facility.dataSource === "Manual" ? (
                    <Chip variant="info">Provided</Chip>
                  ) : facility.geocodeSource === "Census" ? (
                    <Chip variant="info">Looked up</Chip>
                  ) : (
                    <Chip variant="ok">EPA</Chip>
                  )}
                </td>
                {canReview && (
                  <td>
                    <RowActions
                      facility={facility}
                      busy={busyId === facility.registryId}
                      onSet={(status) => void apply(facility, status)}
                      onPick={(mode) => setPicker({ facility, mode })}
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {page && (
        <div className="flex items-center justify-between mt-3 text-[13px] text-rs-light-gray">
          <span>
            {pageRange(page.total, PAGE_SIZE, offset)}
            {loadedAt ? ` · ${updatedLabel(loadedAt)}` : ""}
          </span>
          <div className="flex gap-2">
            <Btn variant="ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
              Previous
            </Btn>
            <Btn
              variant="ghost"
              disabled={offset + PAGE_SIZE >= page.total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </Btn>
          </div>
        </div>
      )}
      {picker && (
        <GroupPickerDialog
          facilityName={picker.facility.name}
          originalName={picker.facility.originalName}
          confirmLabel={picker.mode === "verify" ? "Verify" : "Save Groups"}
          confirmIcon={picker.mode === "verify" ? "verified" : "check"}
          initialIds={picker.facility.polymerGroups.map((g) => g.polymerGroupId)}
          canAdd={canReview}
          locationRegistryId={picker.mode === "verify" ? picker.facility.registryId : undefined}
          canCheckLocation={canReview}
          onLocationApplied={mergeUpdated}
          onConfirm={confirmPick}
          onClose={() => setPicker(null)}
        />
      )}
    </>
  );
}
